import React, { ReactNode } from 'react';
import Row from 'react-bootstrap/Row';
import styled from 'styled-components';

import ContactHelper from '../core/libs/ContactHelper';
import Image, { UserProfilePhoto } from '../core/Image';
import PersonalInfo from './contactInfo/PersonalInfo';
import ProfessionalInfo from './contactInfo/ProfessionalInfo';
import OtherInfo from './contactInfo/OtherInfo';
import { useContactsContext } from '../contexts/ContactsContext';
import {
  ViewContactHeaderLCM,
  ViewContactHeaderMain,
  ViewContactNameContainer,
  ViewContactNameLCM
} from './styled/ViewContact';

import { TAppAddress, TAppContact, TAppEmail, TAppPhone } from 'TProtocol/common/models';

interface Props {
  contact: TAppContact;
}

function MergeItem(props: Props) {
  const contactsContext = useContactsContext();

  const getAllPhones = (contact: TAppContact) => {
    const phones: string[] = [];
    if (contact.primaryMobile) {
      addPhone(contact.primaryMobile, phones);
    }
    if (contact.personalMobile) {
      addPhone(contact.personalMobile, phones);
    }
    if (contact.personalLandline) {
      addPhone(contact.personalLandline, phones);
    }
    if (contact.professionalMobile) {
      addPhone(contact.professionalMobile, phones);
    }
    if (contact.professionalLandline) {
      addPhone(contact.professionalLandline, phones);
    }
    if (contact.otherPhones) {
      contact.otherPhones.forEach(phone => addPhone(phone, phones));
    }
    return phones;
  };

  const addPhone = (phone: TAppPhone, values: string[]) => {
    if (phone?.displayNumber) {
      values.push(phone.displayNumber);
    }
    return values;
  };

  const getAllEmails = (contact: TAppContact) => {
    const emails: string[] = [];
    if (contact.personalEmail) {
      addEmail(contact.personalEmail, emails);
    }
    if (contact.professionalEmail) {
      addEmail(contact.professionalEmail, emails);
    }
    contact.otherEmails?.forEach(email => addEmail(email, emails));
    return emails;
  };

  const addEmail = (email: TAppEmail, values: string[]) => {
    if (email && email.displayEmail) {
      values.push(email.displayEmail);
    }
    return values;
  };

  const getAllAddresses = (contact: TAppContact) => {
    const addresses: string[] = [];
    if (contact.personalAddress) {
      addAddress(contact.personalAddress, addresses);
    }
    if (contact.professionalAddress) {
      addAddress(contact.professionalAddress, addresses);
    }
    if (contact.otherAddresses) {
      contact.otherAddresses.forEach(addr => addAddress(addr, addresses));
    }
    return addresses;
  };

  const addAddress = (address: TAppAddress, values: string[]) => {
    if (address?.address1 && address?.city && address?.admin1 && address?.postalCode) {
      const line2: string = address.city + ' ' + address.admin1 + ' ' + address.postalCode;
      values.push(address.address1);
      values.push(line2);
    }
    return values;
  };

  const getProfessionalInfo = (contact: TAppContact) => {
    const info: string[] = [];
    if (contact.primaryProfessionalInfo?.companyName) {
      info.push(contact.primaryProfessionalInfo.companyName);
    }
    if (contact.primaryProfessionalInfo?.jobTitle) {
      info.push(contact.primaryProfessionalInfo.jobTitle);
    }
    return info;
  };

  const hasPersonalInfo = () => {
    return props.contact?.personalEmail?.displayEmail || props.contact?.personalMobile?.displayNumber ||
      props.contact?.personalLandline?.displayNumber || props.contact?.birthday ||
      props.contact?.personalAddress?.address1;
  };

  const hasProfessionalInfo = () => {
    return props.contact?.primaryProfessionalInfo?.jobTitle || props.contact?.primaryProfessionalInfo?.companyName ||
      props.contact?.professionalEmail?.displayEmail || props.contact?.professionalMobile?.displayNumber ||
      props.contact?.professionalLandline?.displayNumber || props.contact?.professionalAddress?.address1 ||
      (props.contact?.professionalThirdPartyIds?.length ?? 0) > 0;
  };

  const hasOtherInfo = () => {
    return (props.contact?.otherEmails?.length ?? 0) > 0 || (props.contact?.otherPhones?.length ?? 0) > 0 ||
      (props.contact?.otherAddresses?.length ?? 0) > 0;
  };

  const onSelect = () => {
    contactsContext.setSelectedContact(props.contact);
  };


  const contact: TAppContact = props.contact;

  const phones = getAllPhones(contact);
  const emails = getAllEmails(contact);
  const addresses = getAllAddresses(contact);
  const profInfo = getProfessionalInfo(contact);

  const finalValues: string[] = [];
  phones.forEach(phone => finalValues.push(phone));
  emails.forEach(email => finalValues.push(email));
  addresses.forEach(addr => finalValues.push(addr));
  profInfo.forEach(info => finalValues.push(info));

  let otherInfoHeader: ReactNode = null;
  if (hasOtherInfo() && (hasPersonalInfo() || hasProfessionalInfo())) {
    otherInfoHeader = <ViewContactFieldLabel>Other Info</ViewContactFieldLabel>;
  }

  return (
    <MergeItemContainer onClick={onSelect}>

      <Row>
        <ViewContactHeaderLCM>
          <ViewContactHeaderMain>
            <Image src={contact.photoUrl ?? ''} showSpinner={true}/>
            <ViewContactNameContainer>
              <ViewContactNameLCM>{ContactHelper.getName(contact)}</ViewContactNameLCM>
              {contact.primaryMobile?.displayNumber &&
                <div>{contact.primaryMobile.displayNumber}</div>
              }
            </ViewContactNameContainer>
          </ViewContactHeaderMain>
        </ViewContactHeaderLCM>
        <div className="mt-2">
          {hasPersonalInfo() && <>
            <ViewContactFieldLabel>Personal Info</ViewContactFieldLabel>
            <PersonalInfo contact={contact}/>
          </>}

          {hasProfessionalInfo() && <>
            <ViewContactFieldLabel>Professional Info</ViewContactFieldLabel>
            <ProfessionalInfo contact={contact}/>
          </>}

          {hasOtherInfo() && <>
            {otherInfoHeader}
            <OtherInfo contact={contact}/>
          </>}
        </div>
      </Row>
    </MergeItemContainer>
  );
}

const MergeItemContainer = styled.div`
  cursor: pointer;
  box-shadow: 1px 2px 4px 1px rgba(0, 0, 0, 0.1);
  border-radius: 5px;
  height: 100%;
  padding: 6px;
  margin: 4px;

  ${UserProfilePhoto} {
    margin-top: 9px;
    margin-left: 5px;
  }
`;

const ViewContactFieldLabel = styled.div`
  margin-left: 6px;
  font-size: 16px;
`;

export default MergeItem;
